import { SeedLayerDTO, SelectNameValue } from 'business/models/CustomModels';
import EICheckBox, {
  EISelectBox,
  UnitTextBox,
} from 'components/customComponents/Layers/EICheckBox';
import LayersHeading from 'components/customComponents/Layers/LayersHeading';
import ELayersView, { EGridVaryAlong } from 'enums/ELayersView';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FmlxButton } from 'fmlx-common-ui';
import { useRMSelector, useRMDispatch, usePreserveExperiment } from 'redux/hooks';
import { LayerDTO, SeedLayerDetailsDTO } from 'business/models/Models';
import LayersError from 'components/customComponents/Layers/LayersError';
import {
  checkIsValidInput,
  GetSeedName,
  setRoundedValue,
  showAlertSnackBar,
} from 'utils/Global';
import {
  selectExperimentLayers,
  selectSelectedLayerId,
  selectSelectedLayer,
  selectExp,
} from '../../../redux/selectors/ExperimentStateSelector';
import { cloneDeep } from 'lodash';
import { updateExperimentLayers } from 'redux/reducers/experimentDataReducer';
import { withTabBackButton, WithTabBackButtonStateProps } from 'components/tab';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';

interface EditSeedProps extends WithTabBackButtonStateProps {
  setContentView: (contentView: ELayersView) => void;
  selectedSeed: any;
  editSeedData: any;
  setEditSeedData: (...args) => void;
  selectedSeedName: string;
  containerMaxDropVol: number;
  handleBackToPreviousScreen: (contentView: ELayersView) => () => void;
  setSelectedTab: (val: number) => void;
  shouldReturnToLayersExplorer?: boolean;
  onApplyComplete?: () => void;
  selectedSeedDeleted?: boolean;
}
function EditSeed(props: EditSeedProps) {
  const {
    setContentView,
    editSeedData,
    setEditSeedData,
    containerMaxDropVol,
    handleBackToPreviousScreen,
    setHandleOnTabBackPress,
    setSelectedTab,
    shouldReturnToLayersExplorer = false,
    onApplyComplete,
    selectedSeedDeleted = false,
  } = props;
  const [startConcError, setStartConcError] = useState<boolean>(false);
  const [stopConcError, setStopConcError] = useState<boolean>(false);
  const seedVaryAlong: SelectNameValue[] = [
    { name: 'None', value: EGridVaryAlong.NONE },
    { name: 'Rows', value: EGridVaryAlong.ROW },
    { name: 'Columns', value: EGridVaryAlong.COLUMN },
    { name: 'Well Numbers', value: EGridVaryAlong.WELLNUMBER },
  ];

  useEffect(() => {
    setHandleOnTabBackPress(() => handleBackToPreviousScreen(ELayersView.SELECTSEEDS));
  }, [setHandleOnTabBackPress]);

  const dispatch = useRMDispatch();
  const _selectedLayerId = useRMSelector(selectSelectedLayerId);
  const _layers = useRMSelector(selectExperimentLayers);
  const _selectedLayer = useRMSelector(selectSelectedLayer);
  const exp = useRMSelector(selectExp);
  const _setLayers = useCallback(
    (lyrs) => dispatch(updateExperimentLayers(lyrs)),
    [dispatch],
  );

  const callPreserveUndoableState = usePreserveExperiment();

  const [concentrationVaryAlong, setConcentrationVaryAlong] = useState(
    editSeedData.seedDropVolumeVaryAlong,
  );

  const [startConcentration, setStartConcentration] = useState(() => {
    if (
      editSeedData.startSeedDropVolume === null &&
      exp?.containerDefSeedVolume != null
    ) {
      return exp.containerDefSeedVolume;
    }
    return editSeedData.startSeedDropVolume;
  });

  const [stopConcentration, setStopConcentration] = useState(() => {
    if (editSeedData.stopSeedDropVolume === null && exp?.containerDefSeedVolume) {
      return exp.containerDefSeedVolume;
    }
    return editSeedData.stopSeedDropVolume;
  });

  const seedName = useMemo(() => {
    return GetSeedName(editSeedData?.seedId);
  }, [editSeedData?.seedId]);

  function changeConcentrationVaryAlong(e) {
    setConcentrationVaryAlong(Number(e.target.value));
  }

  function handleStartConcentration(e) {
    let concValue = e.target.value;
    const isValid = checkIsValidInput(concValue);
    if (concValue == '.') {
      concValue = '0.';
    }
    if (isValid) {
      setRoundedValue(concValue, setStartConcentration);
    }
    if (concValue > containerMaxDropVol || e.target.value === '') {
      setStartConcError(true);
    } else {
      setStartConcError(false);
    }
  }

  function handleStopConcentration(e) {
    let concValue = e.target.value;
    const isValid = checkIsValidInput(concValue);
    if (concValue == '.') {
      concValue = '0.';
    }
    if (isValid) {
      setRoundedValue(concValue, setStopConcentration);
    }
    if (concValue > containerMaxDropVol || e.target.value === '') {
      setStopConcError(true);
    } else {
      setStopConcError(false);
    }
  }

  function applySeed() {
    if (selectedSeedDeleted) {
      showAlertSnackBar(
        EAlertType.WARNING,
        ESnackBarPosition.TOP,
        'Selected seed has been deleted.',
      );
      return;
    }
    applySeedData();
    setHandleOnTabBackPress(null);
  }

  function applySeedData() {
    const selectedLayer = cloneDeep(_layers).find((lyr) => lyr.id == _selectedLayerId);
    const seedLayerDetail: SeedLayerDetailsDTO =
      selectedLayer.layerDetail as SeedLayerDetailsDTO;
    seedLayerDetail.seedDropVolumeVaryAlong = concentrationVaryAlong;
    seedLayerDetail.startSeedDropVolume = parseFloat(startConcentration);
    seedLayerDetail.stopSeedDropVolume = parseFloat(stopConcentration);
    seedLayerDetail.seedId = editSeedData.seedId;
    const index = _layers.findIndex((l) => l.id == _selectedLayer.id);
    const tempLayers = cloneDeep(_layers);
    tempLayers[index].dirty = true;
    tempLayers[index].layerDetail = seedLayerDetail;
    callPreserveUndoableState();
    _setLayers(tempLayers);
    if (setSelectedTab) {
      setSelectedTab(1);
    }
    handleBackToPreviousScreen(ELayersView.LAYERDESIGNVIEW)();

    if (shouldReturnToLayersExplorer && onApplyComplete) {
      onApplyComplete();
    }
  }

  function selectSeed() {
    const selectedLayer: LayerDTO = [..._layers].find(
      (lyr) => lyr.id == _selectedLayerId,
    );
    const seedLayerDTO: SeedLayerDTO = new SeedLayerDTO();
    seedLayerDTO.layerName = selectedLayer.name;
    seedLayerDTO.layerId = selectedLayer.id;
    seedLayerDTO.seedDropVolumeVaryAlong = concentrationVaryAlong;
    seedLayerDTO.startSeedDropVolume = startConcentration;
    seedLayerDTO.stopSeedDropVolume = stopConcentration;

    const seedLayerDetail: SeedLayerDetailsDTO =
      selectedLayer.layerDetail as SeedLayerDetailsDTO;

    seedLayerDTO.seedId = seedLayerDetail.seedId;

    setEditSeedData(seedLayerDTO);

    setContentView(ELayersView.SELECTSEEDS);
  }
  function hasAnyError() {
    return startConcError || stopConcError;
  }
  return (
    <div
      id="commandpanel-seedinglayer-editseed-wrapper"
      className="flex flex-column flex-x-center"
      style={{ padding: 24 }}
    >
      <EICheckBox
        labelText="Name"
        textBoxText={seedName || 'Select Seed'}
        onClick={() => selectSeed()}
      />

      <LayersHeading text="Seed Volume" />

      <EISelectBox
        labelText="Vary Along"
        selectedValue={concentrationVaryAlong}
        onChange={changeConcentrationVaryAlong}
        options={seedVaryAlong}
      />
      <div
        id="commandpanel-seedinglayer-editseed-unittextbox-concentration"
        className="w-100pr flex"
      >
        <UnitTextBox
          unit="µL"
          labelText="Start"
          inputValue={startConcentration}
          onChange={handleStartConcentration}
          error={startConcError}
        />
        {concentrationVaryAlong !== EGridVaryAlong.NONE && (
          <UnitTextBox
            unit="µL"
            labelText="Stop"
            inputValue={stopConcentration}
            onChange={handleStopConcentration}
            error={stopConcError}
          />
        )}
      </div>
      <div
        id="commandpanel-seedinglayer-editseed-error-handler"
        className="flex"
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginTop: '5px',
        }}
      >
        {hasAnyError() && (
          <LayersError
            errorText="Some of the data you entered is invalid"
            style={{ marginTop: '1rem' }}
          />
        )}
        <FmlxButton
          id="commandpanel-seedinglayer-editseed-error-button"
          style={{
            background: '#008F40',
            color: '#ffffff',
            display: 'flex',
            justifyContent: 'flex-end',
          }}
          disabled={hasAnyError()}
          onClick={applySeed}
          label="Apply"
          type={FmlxButton.Type.POSITIVE}
        />
      </div>
    </div>
  );
}

export default withTabBackButton(EditSeed);
